// client/src/components/HallOfFame.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaTrophy, FaMedal, FaFutbol, FaArrowLeft, FaChartLine, FaUsers, FaStar, FaFire, FaCrown, FaAward, FaFutbol as FaBall } from 'react-icons/fa';
import { GiSoccerField, GiTrophyCup } from 'react-icons/gi';
import API from '../services/api';

const HallOfFame = ({ onBack }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('matches');
    const [hoveredIndex, setHoveredIndex] = useState(null);

    // Helper to get display name (team_name if available, otherwise username)
    const getDisplayName = (player) => {
        return player.team_name || player.username;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await API.get('/hall-of-fame');
                setData(res.data.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center" dir={i18n.dir()}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                    className="text-yellow-400 text-6xl"
                >
                    <FaFutbol />
                </motion.div>
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="ml-4 text-white text-xl font-mono"
                >
                    {t('hallOfFame.loading')}
                </motion.p>
            </div>
        );
    }

    if (!data) return null;

    const categories = {
        matches: [
            { key: 'matches_100', titleKey: 'hallOfFame.categories.matches_100', icon: <FaFutbol />, threshold: 100, data: data.matchMilestones?.matches_100 || [] },
            { key: 'matches_200', titleKey: 'hallOfFame.categories.matches_200', icon: <FaFutbol />, threshold: 200, data: data.matchMilestones?.matches_200 || [] },
            { key: 'matches_500', titleKey: 'hallOfFame.categories.matches_500', icon: <FaFutbol />, threshold: 500, data: data.matchMilestones?.matches_500 || [] },
            { key: 'matches_1000', titleKey: 'hallOfFame.categories.matches_1000', icon: <FaFutbol />, threshold: 1000, data: data.matchMilestones?.matches_1000 || [] }
        ],
        wins: [
            { key: 'wins_100', titleKey: 'hallOfFame.categories.wins_100', icon: <FaFire />, threshold: 100, data: data.winMilestones?.wins_100 || [] },
            { key: 'wins_500', titleKey: 'hallOfFame.categories.wins_500', icon: <FaFire />, threshold: 500, data: data.winMilestones?.wins_500 || [] },
            { key: 'wins_1000', titleKey: 'hallOfFame.categories.wins_1000', icon: <FaFire />, threshold: 1000, data: data.winMilestones?.wins_1000 || [] }
        ],
        goals: [
            { key: 'goals_1000', titleKey: 'hallOfFame.categories.goals_1000', icon: <FaChartLine />, threshold: 1000, data: data.goalsMilestones?.goals_1000 || [] }
        ],
        leagues: [
            { key: 'domestic', titleKey: 'hallOfFame.tabs.leagues', icon: <FaTrophy />, data: data.leagueWinners?.domestic || [] },
            { key: 'champions', titleKey: 'hallOfFame.tabs.leagues', icon: <FaCrown />, data: data.leagueWinners?.champions || [] },
            { key: 'europa', titleKey: 'hallOfFame.tabs.leagues', icon: <FaMedal />, data: data.leagueWinners?.europa || [] }
        ]
    };

    const renderList = (items, valueKey = 'total_matches', suffix = 'matches', isLeague = false) => {
        if (!items || items.length === 0) {
            return <div className="text-gray-400 text-center py-8 italic">{t('hallOfFame.noLegends')}</div>;
        }
        return (
            <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                {items.map((player, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.05, type: "spring", stiffness: 200 }}
                        whileHover={{ scale: 1.02, backgroundColor: "rgba(255,255,255,0.1)" }}
                        onHoverStart={() => setHoveredIndex(`${activeTab}-${idx}`)}
                        onHoverEnd={() => setHoveredIndex(null)}
                        className="relative flex justify-between items-center bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-xl p-4 border border-white/10 backdrop-blur-sm overflow-hidden group"
                    >
                        <motion.div
                            initial={{ x: '-100%' }}
                            animate={{ x: hoveredIndex === `${activeTab}-${idx}` ? '100%' : '-100%' }}
                            transition={{ duration: 0.8 }}
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                        />
                        <div className="flex items-center gap-4 z-10">
                            <span className="text-yellow-400 font-black text-2xl w-12">#{idx + 1}</span>
                            <div>
                                {/* Use team_name if available, fallback to username */}
                                <div className="text-white font-bold text-lg">{getDisplayName(player)}</div>
                                {!isLeague && (
                                    <div className="text-yellow-300 text-sm font-mono">
                                        {player[valueKey]} {suffix}
                                    </div>
                                )}
                                {isLeague && (
                                    <div className="text-gray-400 text-xs">{t('hallOfFame.season')} {player.season}</div>
                                )}
                            </div>
                        </div>
                        {!isLeague && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: idx * 0.05 + 0.2, type: "spring" }}
                                className="text-3xl opacity-60 group-hover:opacity-100 transition"
                            >
                                {valueKey === 'total_matches' && <FaFutbol className="text-green-400" />}
                                {valueKey === 'wins' && <FaFire className="text-red-400" />}
                                {valueKey === 'goals_scored' && <FaChartLine className="text-blue-400" />}
                            </motion.div>
                        )}
                        {isLeague && (
                            <motion.div
                                animate={{ rotate: [0, 5, -5, 0] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className="text-2xl text-yellow-500"
                            >
                                <FaAward />
                            </motion.div>
                        )}
                    </motion.div>
                ))}
            </div>
        );
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'matches':
                return (
                    <div className="space-y-8">
                        {categories.matches.map(cat => (
                            <motion.div
                                key={cat.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-full bg-yellow-500/20 text-yellow-400 text-xl">
                                        {cat.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                                        {t(cat.titleKey)}
                                    </h3>
                                </div>
                                {renderList(cat.data, 'total_matches', t('hallOfFame.tabs.matches').toLowerCase())}
                            </motion.div>
                        ))}
                    </div>
                );
            case 'wins':
                return (
                    <div className="space-y-8">
                        {categories.wins.map(cat => (
                            <motion.div
                                key={cat.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-full bg-red-500/20 text-red-400 text-xl">
                                        {cat.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold bg-gradient-to-r from-red-400 to-orange-500 bg-clip-text text-transparent">
                                        {t(cat.titleKey)}
                                    </h3>
                                </div>
                                {renderList(cat.data, 'wins', t('hallOfFame.tabs.wins').toLowerCase())}
                            </motion.div>
                        ))}
                    </div>
                );
            case 'goals':
                return (
                    <div className="space-y-8">
                        {categories.goals.map(cat => (
                            <motion.div
                                key={cat.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-full bg-blue-500/20 text-blue-400 text-xl">
                                        {cat.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-500 bg-clip-text text-transparent">
                                        {t(cat.titleKey)}
                                    </h3>
                                </div>
                                {renderList(cat.data, 'goals_scored', t('hallOfFame.tabs.goals').toLowerCase())}
                            </motion.div>
                        ))}
                    </div>
                );
            case 'leagues':
                return (
                    <div className="space-y-8">
                        {categories.leagues.map(cat => (
                            <motion.div
                                key={cat.key}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-full bg-purple-500/20 text-purple-400 text-xl">
                                        {cat.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                                        {t(cat.titleKey)}
                                    </h3>
                                </div>
                                {renderList(cat.data, null, null, true)}
                            </motion.div>
                        ))}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white font-sans" dir={i18n.dir()}>
            {/* Animated background particles */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute bg-yellow-400/5 rounded-full"
                        style={{
                            width: Math.random() * 100 + 20,
                            height: Math.random() * 100 + 20,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                            y: [0, -30, 0],
                            opacity: [0, 0.3, 0],
                        }}
                        transition={{
                            duration: Math.random() * 5 + 5,
                            repeat: Infinity,
                            delay: Math.random() * 5,
                        }}
                    />
                ))}
            </div>

            {/* Header */}
            <div className="sticky top-0 z-20 backdrop-blur-xl bg-black/40 border-b border-yellow-500/30 px-6 py-4 flex items-center gap-4">
                <motion.button
                    whileHover={{ scale: 1.1, x: isRTL ? 5 : -5 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onBack}
                    className="text-gray-300 hover:text-yellow-400 transition"
                >
                    <FaArrowLeft size={24} />
                </motion.button>
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1, rotate: [0, 10, -10, 0] }}
                    transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                >
                    <GiTrophyCup className="text-4xl text-yellow-400 drop-shadow-lg" />
                </motion.div>
                <motion.h1
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    className="text-3xl md:text-4xl font-black bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent"
                >
                    {t('hallOfFame.title')}
                </motion.h1>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap justify-center gap-3 p-4 border-b border-white/10 sticky top-[72px] z-10 bg-black/30 backdrop-blur-md">
                {[
                    { id: 'matches', label: t('hallOfFame.tabs.matches'), icon: <FaFutbol /> },
                    { id: 'wins', label: t('hallOfFame.tabs.wins'), icon: <FaFire /> },
                    { id: 'goals', label: t('hallOfFame.tabs.goals'), icon: <FaChartLine /> },
                    { id: 'leagues', label: t('hallOfFame.tabs.leagues'), icon: <FaCrown /> }
                ].map(tab => (
                    <motion.button
                        key={tab.id}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActiveTab(tab.id)}
                        className={`relative px-5 py-2 rounded-full text-sm font-bold transition-all flex items-center gap-2 ${
                            activeTab === tab.id
                                ? 'text-yellow-400 bg-yellow-500/20 shadow-lg'
                                : 'text-gray-300 hover:text-white hover:bg-white/10'
                        }`}
                    >
                        {tab.icon} {tab.label}
                        {activeTab === tab.id && (
                            <motion.div
                                layoutId="activeTab"
                                className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400"
                                transition={{ type: "spring", stiffness: 300 }}
                            />
                        )}
                    </motion.button>
                ))}
            </div>

            {/* Content */}
            <div className="relative max-w-6xl mx-auto p-6">
                <motion.div
                    className="absolute inset-0 bg-yellow-500/5 rounded-3xl blur-3xl"
                    animate={{ opacity: [0.3, 0.6, 0.3] }}
                    transition={{ duration: 4, repeat: Infinity }}
                />
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4, type: "spring", stiffness: 100 }}
                        className="relative bg-black/40 backdrop-blur-sm rounded-3xl p-6 border border-yellow-500/20 shadow-2xl overflow-hidden"
                    >
                        <motion.div
                            className="absolute -top-10 -right-10 opacity-10"
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 20, ease: "linear" }}
                        >
                            <FaBall className="text-9xl text-yellow-400" />
                        </motion.div>
                        {renderTabContent()}
                    </motion.div>
                </AnimatePresence>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-8 text-center text-gray-500 text-xs"
                >
                    <p>{t('hallOfFame.footer')}</p>
                    <p className="mt-1">✨ Hover over entries to see animated shine ✨</p>
                </motion.div>
            </div>

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgba(255,255,255,0.05);
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(255,255,0,0.3);
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(255,255,0,0.5);
                }
            `}</style>
        </div>
    );
};

export default HallOfFame;